import type { BookChapter, BookPage } from "../types/book";
import { pictures } from "./lessonContent";

export const bookChapters: BookChapter[] = [
  { id: "ch1", number: 1, title: "Saludos", titleTranslation: "Greetings", status: "read", pages: 6 },
  { id: "ch2", number: 2, title: "En el café", titleTranslation: "At the café", status: "read", pages: 7 },
  { id: "ch3", number: 3, title: "Por la ciudad", titleTranslation: "Around town", status: "read", pages: 8 },
  { id: "ch4", number: 4, title: "En el mercado", titleTranslation: "At the market", status: "reading", pages: 6 },
  { id: "ch5", number: 5, title: "La familia", titleTranslation: "Family", status: "locked", pages: 7 },
  { id: "ch6", number: 6, title: "El fin de semana", titleTranslation: "The weekend", status: "locked", pages: 8 },
];

export const marketChapterPages: BookPage[] = [
  {
    id: "p1",
    kind: "story",
    title: "Sábado en el mercado",
    subtitle: "Saturday at the market",
    image: pictures.market,
    paragraphs: [
      {
        text: "Es sábado por la mañana. Lucía va al mercado con su bolsa grande.",
        translation: "It's Saturday morning. Lucía goes to the market with her big bag.",
      },
      {
        text: "El mercado está lleno de colores: tomates rojos, naranjas y pan fresco.",
        translation: "The market is full of colours: red tomatoes, oranges and fresh bread.",
      },
      {
        text: "Lucía quiere comprar fruta para toda la semana.",
        translation: "Lucía wants to buy fruit for the whole week.",
      },
    ],
  },
  {
    id: "p2",
    kind: "vocab",
    title: "Palabras del mercado",
    subtitle: "Market words",
    vocab: [
      { term: "el mercado", translation: "the market" },
      { term: "la bolsa", translation: "the bag" },
      { term: "la fruta", translation: "the fruit" },
      { term: "las naranjas", translation: "the oranges" },
      { term: "las fresas", translation: "the strawberries" },
      { term: "el pan", translation: "the bread" },
      { term: "la caja", translation: "the box" },
      { term: "el kilo", translation: "the kilo" },
      { term: "barato", translation: "cheap" },
      { term: "dulce", translation: "sweet" },
    ],
  },
  {
    id: "p3",
    kind: "dialogue",
    title: "En la frutería",
    subtitle: "At the fruit stall",
    dialogue: [
      { speaker: "Lucía", text: "Buenos días. ¿Cuánto cuestan las naranjas?", translation: "Good morning. How much are the oranges?" },
      { speaker: "Vendedor", text: "Dos euros el kilo. Son muy dulces.", translation: "Two euros a kilo. They're very sweet." },
      { speaker: "Lucía", text: "Quiero un kilo, por favor. ¿Y las fresas?", translation: "I'd like a kilo, please. And the strawberries?" },
      { speaker: "Vendedor", text: "Tres euros la caja. Hoy están baratas.", translation: "Three euros a box. They're cheap today." },
      { speaker: "Lucía", text: "Perfecto. Una caja de fresas también.", translation: "Perfect. A box of strawberries too." },
    ],
  },
  {
    id: "p4",
    kind: "grammar",
    title: "Un, una, unos, unas",
    subtitle: "Indefinite articles",
    grammarRule:
      "Spanish articles agree with the noun in gender and number. Use un / una for “a”, and unos / unas for “some”.",
    grammar: [
      { form: "un", example: "un kilo", translation: "a kilo" },
      { form: "una", example: "una caja", translation: "a box" },
      { form: "unos", example: "unos tomates", translation: "some tomatoes" },
      { form: "unas", example: "unas fresas", translation: "some strawberries" },
    ],
  },
  {
    id: "p5",
    kind: "culture",
    title: "Los mercados de España",
    subtitle: "Markets in Spain",
    paragraphs: [
      { text: "En España, cada barrio tiene su mercado.", translation: "In Spain, every neighbourhood has its market." },
      { text: "La gente habla con los vendedores y pide consejos.", translation: "People chat with the sellers and ask for advice." },
    ],
    note: "Asking «¿Qué me recomienda?» (What do you recommend?) often gets you the best fruit of the day.",
  },
  {
    id: "p6",
    kind: "grammar",
    title: "¿Cuánto cuesta? ¿Cuánto cuestan?",
    subtitle: "Asking the price",
    grammarRule: "Use cuesta for one thing and cuestan for more than one.",
    grammar: [
      { form: "cuesta", example: "¿Cuánto cuesta el pan?", translation: "How much is the bread?" },
      { form: "cuestan", example: "¿Cuánto cuestan las fresas?", translation: "How much are the strawberries?" },
    ],
  },
];

export const glossary: Record<string, string> = {
  es: "is",
  sábado: "Saturday",
  por: "for / by",
  la: "the",
  mañana: "morning",
  va: "goes",
  al: "to the",
  mercado: "market",
  con: "with",
  su: "her / his",
  bolsa: "bag",
  grande: "big",
  el: "the",
  está: "is",
  lleno: "full",
  de: "of",
  colores: "colours",
  tomates: "tomatoes",
  rojos: "red",
  naranjas: "oranges",
  y: "and",
  pan: "bread",
  fresco: "fresh",
  quiere: "wants",
  comprar: "to buy",
  fruta: "fruit",
  para: "for",
  toda: "all / whole",
  semana: "week",
  buenos: "good",
  días: "days",
  cuánto: "how much",
  cuestan: "cost (plural)",
  cuesta: "costs",
  las: "the (plural)",
  los: "the (plural)",
  dos: "two",
  euros: "euros",
  kilo: "kilo",
  son: "they are",
  muy: "very",
  dulces: "sweet",
  quiero: "I want",
  un: "a",
  una: "a",
  unos: "some",
  unas: "some",
  favor: "favour",
  fresas: "strawberries",
  tres: "three",
  caja: "box",
  hoy: "today",
  están: "they are",
  baratas: "cheap",
  perfecto: "perfect",
  también: "too / also",
  en: "in",
  españa: "Spain",
  cada: "each / every",
  barrio: "neighbourhood",
  tiene: "has",
  gente: "people",
  habla: "talks",
  vendedores: "sellers",
  pide: "asks for",
  consejos: "advice",
};
